import { gql } from '@apollo/client';
import { skipToken } from '@apollo/client/react';
import { useParams } from '@remix-run/react';
import { useAuthenticatedQuery } from 'platform/apollo';
import { useAuthContext } from 'platform/components/AuthContext';

import type {
  SellerJourneyQuery_sellerProducts_SellerProductsQuery_sellerJourney_GeneratingJourney as GeneratingJourney,
  SellerJourneyQuery_sellerProducts_SellerProductsQuery_sellerJourney as SellerJourney,
} from './__generated__/useSellerJourney';
import { SellerJourneyDocument } from './__generated__/useSellerJourney';
import type { SellerLead } from './SellerLeadContext';

export type { SellerJourney, GeneratingJourney };

const extractSellerLead = (sellerJourney: SellerJourney | null): SellerLead | null => {
  if (!sellerJourney) {
    return null;
  }

  return {
    ...sellerJourney.sellerLead,
    address: sellerJourney.address,
  };
};

/**
 * Fetches the seller journey for a property address (and optional customer scope for internal use).
 */
export const useSellerJourney = () => {
  const { customerUUID } = useAuthContext();
  const { addressUUID } = useParams();

  const { data, loading, error, refetch, startPolling, stopPolling } = useAuthenticatedQuery(
    SellerJourneyDocument,
    !addressUUID
      ? skipToken
      : {
          variables: {
            addressUUID,
            ...(customerUUID !== undefined ? { customerUUID } : {}),
          },
        },
  );

  const sellerJourney = data?.sellerProducts?.sellerJourney ?? null;

  return {
    sellerJourney,
    lead: extractSellerLead(sellerJourney),
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  };
};

gql`
  query SellerJourney($addressUUID: String!, $customerUUID: String) {
    sellerProducts {
      sellerJourney(addressUUID: $addressUUID, customerUUID: $customerUUID) {
        __typename

        sellerLead {
          ...SellerLead
        }

        address {
          ...SellerLeadAddress
        }
      }
    }
  }
`;
